import React, { useMemo } from 'react';
import { AuditRecord } from '../../../types';
import {
  computeConfidenceCalibration, computeExpectancyMatrix,
  computeDecisionQuality, computeDecisionQualitySummary,
} from '../../../services/auditService';
import { Th, Td, Section, tbStyle } from './AuditTablePrimitives';
import { rFmt, pFmt } from '../utils/formatters';

function DQTierBadge({ tier }: { tier: 'elite' | 'strong' | 'acceptable' | 'weak' | 'poor' }) {
  const cfg = {
    elite:      { color: 'var(--gold)',    bg: 'var(--gold)18'    },
    strong:     { color: 'var(--green)',   bg: 'var(--green)18'   },
    acceptable: { color: 'var(--cyan)',    bg: 'var(--cyan)18'    },
    weak:       { color: 'var(--amber)',   bg: 'var(--amber)18'   },
    poor:       { color: 'var(--red)',     bg: 'var(--red)18'     },
  }[tier];
  return (
    <span style={{ fontSize: 9, fontWeight: 700, fontFamily: 'var(--mono)',
      color: cfg.color, background: cfg.bg,
      border: `1px solid ${cfg.color}44`, padding: '1px 6px', borderRadius: 3 }}>
      {tier.toUpperCase()}
    </span>
  );
}

function CalibStatusBadge({ status }: { status: 'overconfident' | 'underconfident' | 'well_calibrated' | 'insufficient_data' }) {
  const cfg = {
    well_calibrated:   { color: 'var(--green)',  label: '✓ calibrated' },
    overconfident:     { color: 'var(--red)',    label: '↑ overconfident' },
    underconfident:    { color: 'var(--amber)',  label: '↓ underconfident' },
    insufficient_data: { color: 'var(--t3)',     label: '— no data' },
  }[status];
  return (
    <span style={{ fontSize: 9, fontWeight: 700, color: cfg.color, fontFamily: 'var(--mono)' }}>
      {cfg.label}
    </span>
  );
}

export function IntelligenceView({ auditRecords }: { auditRecords: AuditRecord[] }) {
  const calibration = useMemo(() => computeConfidenceCalibration(auditRecords), [auditRecords]);
  const matrix      = useMemo(() => computeExpectancyMatrix(auditRecords),      [auditRecords]);
  const dqsSummary  = useMemo(() => computeDecisionQualitySummary(auditRecords), [auditRecords]);

  if (!auditRecords.length) {
    return (
      <div style={{ padding: '24px 0', color: 'var(--t3)', textAlign: 'center', fontSize: 11 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t2)', marginBottom: 6 }}>⚡ Quant Intelligence</div>
        No audit records yet. Confidence calibration, expectancy matrix, and decision quality
        analysis will appear here as you build a trade history.
      </div>
    );
  }

  const dqsBucketColor = (tier: string) =>
    ({ elite:'var(--gold)', strong:'var(--green)', acceptable:'var(--cyan)',
       weak:'var(--amber)', poor:'var(--red)' } as Record<string, string>)[tier] ?? 'var(--t2)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      <Section title="📊 Confidence Calibration">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px', minWidth: 90 }}>
            <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)', letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 3 }}>Cal. Score</div>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--mono)',
              color: calibration.overallScore !== null && calibration.overallScore >= 70 ? 'var(--green)'
                : calibration.overallScore !== null && calibration.overallScore >= 50 ? 'var(--amber)' : 'var(--red)' }}>
              {calibration.overallScore !== null ? `${calibration.overallScore}` : '—'}
            </div>
          </div>
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px', minWidth: 90 }}>
            <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)', letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 3 }}>Monotonicity</div>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--cyan)' }}>
              {calibration.monotonicityScore !== null ? `${calibration.monotonicityScore}` : '—'}
            </div>
          </div>
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px', minWidth: 130 }}>
            <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)', letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 3 }}>Overconf. Risk</div>
            <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--mono)',
              color: ({ low:'var(--green)', medium:'var(--amber)', high:'var(--red)', unknown:'var(--t3)' } as Record<string,string>)[calibration.overconfidenceRisk] }}>
              {calibration.overconfidenceRisk.toUpperCase()}
            </div>
          </div>
          <div style={{ fontSize: 10, color: 'var(--t3)', alignSelf: 'center', maxWidth: 280, lineHeight: 1.4 }}>
            {calibration.summary}
          </div>
        </div>

        <table style={tbStyle}>
          <thead>
            <tr><Th>Bucket</Th><Th right>Count</Th><Th right>Win Rate</Th><Th right>Avg R</Th><Th right>Expectancy</Th><Th right>Gap vs Prior</Th><Th>Status</Th></tr>
          </thead>
          <tbody>
            {calibration.buckets.map(b => (
              <tr key={b.bucket}>
                <Td><span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--cyan)', fontSize: 10 }}>{b.bucket}</span></Td>
                <Td right>{b.count}</Td>
                <Td right color={b.winRate !== null ? (b.winRate >= 0.55 ? 'var(--green)' : b.winRate >= 0.45 ? 'var(--amber)' : 'var(--red)') : 'var(--t3)'}>{pFmt(b.winRate)}</Td>
                <Td right color={b.avgR !== null ? (b.avgR > 0 ? 'var(--green)' : b.avgR < 0 ? 'var(--red)' : 'var(--t2)') : 'var(--t3)'}>{rFmt(b.avgR)}</Td>
                <Td right color={b.expectancy !== null ? (b.expectancy > 0 ? 'var(--green)' : 'var(--red)') : 'var(--t3)'}>{rFmt(b.expectancy)}</Td>
                <Td right color={b.calibrationGap !== null ? (b.calibrationGap >= 0 ? 'var(--green)' : 'var(--red)') : 'var(--t3)'}>{b.calibrationGap !== null ? rFmt(b.calibrationGap) : '—'}</Td>
                <Td><CalibStatusBadge status={b.status} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 6, fontStyle: 'italic' }}>
          Gap vs Prior = avgR difference vs prior confidence tier. Positive = well-calibrated. Min 3 closed records per bucket required.
        </div>
      </Section>

      <Section title="🧮 Setup Expectancy Matrix">
        <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 8 }}>
          {matrix.cellsWithData} contextual edges across {matrix.totalCells} setup × regime × phase combinations.
          {matrix.cellsWithData === 0 && ' Build more closed records to see contextual edge analysis.'}
        </div>
        {matrix.bestCells.length > 0 && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--green)', marginBottom: 4 }}>▲ Strongest Contextual Edges (top {matrix.bestCells.length})</div>
            <table style={tbStyle}>
              <thead><tr><Th>Setup</Th><Th>Regime</Th><Th>Phase</Th><Th right>N</Th><Th right>Win %</Th><Th right>Avg R</Th><Th right>Expectancy</Th><Th>Quality</Th></tr></thead>
              <tbody>
                {matrix.bestCells.map((c, i) => (
                  <tr key={i}>
                    <Td><span style={{ fontSize: 9 }}>{c.setupType.substring(0, 22)}</span></Td>
                    <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{c.regime.substring(0, 14)}</span></Td>
                    <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{c.phase}</span></Td>
                    <Td right>{c.count}</Td>
                    <Td right color={c.winRate !== null ? (c.winRate >= 0.55 ? 'var(--green)' : 'var(--amber)') : 'var(--t3)'}>{pFmt(c.winRate)}</Td>
                    <Td right color={c.avgR !== null ? (c.avgR > 0 ? 'var(--green)' : 'var(--red)') : 'var(--t3)'}>{rFmt(c.avgR)}</Td>
                    <Td right color={c.expectancy !== null ? (c.expectancy > 0 ? 'var(--green)' : 'var(--red)') : 'var(--t3)'}>{rFmt(c.expectancy)}</Td>
                    <Td><span style={{ fontSize: 9, fontWeight: 700, color: ({ high:'var(--green)', medium:'var(--amber)', low:'var(--red)', insufficient_data:'var(--t3)' } as Record<string,string>)[c.quality] }}>{c.quality.replace('_', ' ')}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {matrix.worstCells.length > 0 && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--red)', marginTop: 10, marginBottom: 4 }}>▼ Weakest Contextual Edges (bottom {matrix.worstCells.length})</div>
            <table style={tbStyle}>
              <thead><tr><Th>Setup</Th><Th>Regime</Th><Th>Phase</Th><Th right>N</Th><Th right>Win %</Th><Th right>Avg R</Th><Th right>Expectancy</Th><Th>Quality</Th></tr></thead>
              <tbody>
                {matrix.worstCells.map((c, i) => (
                  <tr key={i}>
                    <Td><span style={{ fontSize: 9 }}>{c.setupType.substring(0, 22)}</span></Td>
                    <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{c.regime.substring(0, 14)}</span></Td>
                    <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{c.phase}</span></Td>
                    <Td right>{c.count}</Td>
                    <Td right color={c.winRate !== null ? (c.winRate >= 0.55 ? 'var(--green)' : 'var(--amber)') : 'var(--t3)'}>{pFmt(c.winRate)}</Td>
                    <Td right color={c.avgR !== null ? (c.avgR > 0 ? 'var(--green)' : 'var(--red)') : 'var(--t3)'}>{rFmt(c.avgR)}</Td>
                    <Td right color={c.expectancy !== null ? (c.expectancy > 0 ? 'var(--green)' : 'var(--red)') : 'var(--t3)'}>{rFmt(c.expectancy)}</Td>
                    <Td><span style={{ fontSize: 9, fontWeight: 700, color: ({ high:'var(--green)', medium:'var(--amber)', low:'var(--red)', insufficient_data:'var(--t3)' } as Record<string,string>)[c.quality] }}>{c.quality.replace('_', ' ')}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 6, fontStyle: 'italic' }}>
          Quality labels require ≥3 closed records per context. Rankings are sample-weighted. Raw expectancy shown. Observational only.
        </div>
      </Section>

      <Section title="🎯 Decision Quality Score (DQS)">
        <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 10, lineHeight: 1.5, maxWidth: 600 }}>
          DQS evaluates <strong>decision process quality</strong> independently of outcome.
          Scores reflect context alignment, discipline, structure, risk-reward coherence, and readiness.
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px', minWidth: 90 }}>
            <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)', letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 3 }}>Avg DQS</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--mono)',
              color: dqsSummary.avgScore !== null && dqsSummary.avgScore >= 68 ? 'var(--green)'
                : dqsSummary.avgScore !== null && dqsSummary.avgScore >= 52 ? 'var(--amber)' : 'var(--red)' }}>
              {dqsSummary.avgScore ?? '—'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {(Object.entries(dqsSummary.byTier) as Array<[string, number]>)
              .filter(([, n]) => n > 0)
              .map(([tier, n]) => (
                <div key={tier} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                  background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px' }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: dqsBucketColor(tier), fontFamily: 'var(--mono)' }}>{n}</span>
                  <DQTierBadge tier={tier as any} />
                </div>
              ))}
          </div>
        </div>

        {Object.keys(dqsSummary.bySetupType).length > 0 && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--t2)', marginBottom: 4 }}>By Setup Type</div>
            <table style={tbStyle}>
              <thead><tr><Th>Setup Type</Th><Th right>N</Th><Th right>Avg DQS</Th></tr></thead>
              <tbody>
                {Object.entries(dqsSummary.bySetupType)
                  .sort((a, b) => b[1].avgScore - a[1].avgScore)
                  .map(([st, s]) => (
                    <tr key={st}>
                      <Td><span style={{ fontSize: 9 }}>{st.substring(0, 28)}</span></Td>
                      <Td right>{s.count}</Td>
                      <Td right color={s.avgScore >= 68 ? 'var(--green)' : s.avgScore >= 52 ? 'var(--amber)' : 'var(--red)'}>{s.avgScore}</Td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </>
        )}

        {auditRecords.length > 0 && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--t2)', marginTop: 10, marginBottom: 4 }}>
              Recent Decisions — DQS Detail (last 10)
            </div>
            <table style={tbStyle}>
              <thead>
                <tr><Th>Symbol</Th><Th>Setup</Th><Th>Outcome</Th><Th right>DQS</Th><Th>Tier</Th><Th right>Ctx</Th><Th right>Disc</Th><Th right>Struct</Th><Th right>RR</Th><Th right>Ready</Th></tr>
              </thead>
              <tbody>
                {[...auditRecords].sort((a, b) => b.createdAt - a.createdAt).slice(0, 10).map(r => {
                  const dq = computeDecisionQuality(r);
                  return (
                    <tr key={r.id}>
                      <Td><span style={{ fontWeight: 700 }}>{r.symbol}</span></Td>
                      <Td><span style={{ fontSize: 9 }}>{r.setupType.substring(0, 18)}</span></Td>
                      <Td><span style={{ fontSize: 9, color: r.outcome === 'win' ? 'var(--green)' : r.outcome === 'loss' ? 'var(--red)' : 'var(--t3)' }}>{r.outcome}</span></Td>
                      <Td right color={dq.score >= 68 ? 'var(--green)' : dq.score >= 52 ? 'var(--amber)' : 'var(--red)'}><strong>{dq.score}</strong></Td>
                      <Td><DQTierBadge tier={dq.tier} /></Td>
                      <Td right>{dq.breakdown.contextAlignment}</Td>
                      <Td right>{dq.breakdown.confidenceDiscipline}</Td>
                      <Td right>{dq.breakdown.structureQuality}</Td>
                      <Td right>{dq.breakdown.riskQuality}</Td>
                      <Td right>{dq.breakdown.executionReadiness}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        {(dqsSummary.topDecisions.length > 0 || dqsSummary.bottomDecisions.length > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
            {[
              { label: '🏆 Strongest Decision Process (DQS)', data: dqsSummary.topDecisions, color: 'var(--green)' },
              { label: '⚠ Weakest Decision Process (DQS)', data: dqsSummary.bottomDecisions, color: 'var(--amber)' },
            ].map(({ label, data, color }) => (
              <div key={label} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--t2)', marginBottom: 6 }}>{label}</div>
                {data.map(d => (
                  <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '3px 0', borderBottom: '1px solid var(--border)22' }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 10, color: 'var(--t1)' }}>{d.symbol}</span>
                      <span style={{ fontSize: 9, color: 'var(--t3)', marginLeft: 6 }}>{d.setupType.substring(0, 18)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 12, color }}>{d.score}</span>
                      <DQTierBadge tier={d.tier} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 8, fontStyle: 'italic' }}>
          DQS is observational and evaluates decision process, not outcome. All figures are analytical context only.
        </div>
      </Section>
    </div>
  );
}
